// Helpdesk Phase 1 — API provider.
//
// GREENFIELD APP — this file talks only to /v1/helpdesk/* routes
// (backend-go/internal/modules/helpdesk). Do NOT import from or reuse
// src/providers used by src/content/applications/Tickets/ or .../WorkLog/.
//
// Mirrors the base-api request/response pattern used by
// src/providers/reports-api.ts: every call goes through BaseAPI.request
// (auth headers + base URL resolution + the shared axios error interceptor
// live there), and handleResponse() unwraps `{ data }` / throws on error
// responses so callers can just `await`.
import { BaseAPI } from './base-api';
import {
  Ticket,
  TicketMessage,
  Desk,
  DeskDetail,
  CategoryNode,
  DeskAgent,
  DeskField,
  TimeLogEntry,
  TimeLogSummary,
  TimerResponse,
  StopTimerResponse,
  SearchTicketsRequest,
  SearchTicketsResponse,
  StatusCountsResponse,
  QueueCountsResponse,
  CreateTicketRequest,
  CreateWidgetTicketRequest,
  UpdateTicketRequest,
  CreateMessageRequest,
  LogTimeRequest,
  UpdateStatusRequest,
  AssignRequest,
  CreateDeskRequest,
  UpdateDeskRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  UpsertAgentRequest,
  CreateDeskFieldRequest,
  UpdateDeskFieldRequest,
  CreateLinkRequest,
  TicketLinkResponse,
  TicketStatus,
  Skill,
  CreateSkillRequest,
  Approval,
  CreateApprovalRequest,
  DecideRequest,
  HandoverRequest,
  SlaPolicy,
  CreateSlaPolicyRequest,
  UpdateSlaPolicyRequest,
  RoutingRule,
  CreateRoutingRuleRequest,
  UpdateRoutingRuleRequest,
  CannedResponse,
  CreateCannedResponseRequest,
  UpdateCannedResponseRequest,
  IntakeForm,
  CreateIntakeFormRequest,
  UpdateIntakeFormRequest,
  SubmitIntakeTicketRequest,
  SubmitCsatRequest,
  CsatResponse,
  CsatAggregateResponse,
  TemplateDesk,
  TemplateDeskTree,
  CreateDeskFromTemplateRequest,
  EmailIdentity,
  CreateEmailIdentityRequest,
  UpdateEmailIdentityRequest,
  Connector,
  CreateConnectorRequest,
  UpdateConnectorRequest
} from 'src/content/applications/Helpdesk/types';

const handleResponse = (response: any) => {
  if (!response) {
    throw new Error('Network error or server is unreachable');
  }
  if (response.status && response.status >= 400) {
    throw new Error(response.message || `Request failed with status ${response.status}`);
  }
  if ('message' in response && !('data' in response)) {
    throw new Error(response.message || 'Request failed');
  }
  return response.data;
};

export const HelpdeskAPI = {
  // ---------------------------------------------------------------------
  // Tickets — core CRUD + search
  // ---------------------------------------------------------------------

  searchTickets: async (req: SearchTicketsRequest): Promise<SearchTicketsResponse> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/tickets/search', req);
    return handleResponse(response);
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/tickets/${id}`);
    return handleResponse(response);
  },

  createTicket: async (req: CreateTicketRequest): Promise<Ticket> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/tickets', req);
    return handleResponse(response);
  },

  updateTicket: async (id: string, req: UpdateTicketRequest): Promise<Ticket> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/tickets/${id}`, req);
    return handleResponse(response);
  },

  // submitWidgetTicket maps to POST /v1/helpdesk/widget/tickets (Phase 3
  // in-product feedback widget — FeedbackWidget.tsx). Authenticated, same
  // JWT/tenant headers as every other /v1/helpdesk/* call; desk resolution
  // and permission gating happen server-side.
  submitWidgetTicket: async (req: CreateWidgetTicketRequest): Promise<Ticket> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/widget/tickets', req);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Transitions — status / assign / links
  // ---------------------------------------------------------------------

  setStatus: async (id: string, status: TicketStatus, substatus?: string | null): Promise<Ticket> => {
    const req: UpdateStatusRequest = { status, substatus: substatus ?? undefined };
    const response = await BaseAPI.request('PATCH', `/v1/helpdesk/tickets/${id}/status`, req);
    return handleResponse(response);
  },

  assign: async (id: string, assigneePartyId: number | null): Promise<Ticket> => {
    const req: AssignRequest = { assigneePartyId };
    const response = await BaseAPI.request('PATCH', `/v1/helpdesk/tickets/${id}/assign`, req);
    return handleResponse(response);
  },

  listLinks: async (id: string): Promise<TicketLinkResponse[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/tickets/${id}/links`);
    return handleResponse(response);
  },

  createLink: async (id: string, req: CreateLinkRequest): Promise<TicketLinkResponse> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/tickets/${id}/links`, req);
    return handleResponse(response);
  },

  deleteLink: async (id: string, linkId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/tickets/${id}/links/${linkId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Messages — replies / internal notes
  // ---------------------------------------------------------------------

  listMessages: async (id: string): Promise<TicketMessage[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/tickets/${id}/messages`);
    return handleResponse(response);
  },

  postMessage: async (id: string, req: CreateMessageRequest): Promise<TicketMessage> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/tickets/${id}/messages`, req);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Desks / categories / agents / fields (admin config surface)
  // ---------------------------------------------------------------------

  listDesks: async (): Promise<Desk[]> => {
    const response = await BaseAPI.request('GET', '/v1/helpdesk/desks');
    return handleResponse(response);
  },

  // myDesks maps to GET /v1/helpdesk/my-desks (G5) — returns only the active
  // desks visible to the caller's audience kind, for the requester-facing
  // New Ticket desk picker. Unlike listDesks (admin config surface, all
  // desks), this is the requester-safe list.
  myDesks: async (): Promise<Desk[]> => {
    const response = await BaseAPI.request('GET', '/v1/helpdesk/my-desks');
    return handleResponse(response);
  },

  // getPortalDesks maps to GET /v1/helpdesk/portal/desks (Wave V / client
  // portal helpdesk surface). Server-resolves the caller's client-portal
  // audience and returns only desks whose audience includes 'client',
  // tenant-scoped; 403 if the caller is not a client-portal caller. Distinct
  // from myDesks() (employee/internal-portal audience) — the client portal
  // uses its own endpoint so a Client/Client Admin JWT never has to be
  // trusted to resolve the correct audience kind client-side.
  getPortalDesks: async (): Promise<Desk[]> => {
    const response = await BaseAPI.request('GET', '/v1/helpdesk/portal/desks');
    return handleResponse(response);
  },

  getDesk: async (id: number): Promise<DeskDetail> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${id}`);
    return handleResponse(response);
  },

  // saveDesk creates a new desk when `id` is omitted, otherwise updates the
  // existing desk. Mirrors POST /desks vs PUT /desks/:id on the backend.
  saveDesk: async (id: number | null, req: CreateDeskRequest | UpdateDeskRequest): Promise<Desk> => {
    const response = id
      ? await BaseAPI.request('PUT', `/v1/helpdesk/desks/${id}`, req)
      : await BaseAPI.request('POST', '/v1/helpdesk/desks', req);
    return handleResponse(response);
  },

  listCategoryTree: async (deskId: number): Promise<CategoryNode[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/categories/tree`);
    return handleResponse(response);
  },

  // saveCategory creates a new category when `id` is omitted, otherwise
  // updates the existing category. Mirrors POST /categories vs
  // PUT /categories/:id on the backend.
  saveCategory: async (id: number | null, req: CreateCategoryRequest | UpdateCategoryRequest): Promise<CategoryNode> => {
    const response = id
      ? await BaseAPI.request('PUT', `/v1/helpdesk/categories/${id}`, req)
      : await BaseAPI.request('POST', '/v1/helpdesk/categories', req);
    return handleResponse(response);
  },

  // deleteCategory maps to DELETE /v1/helpdesk/categories/:id
  // (backend-go/internal/modules/helpdesk/helpdesk.go, handlers/config_handler.go
  // DeleteCategory — returns 204 No Content; the backend refuses deletion
  // when the category has children or open tickets and surfaces that as an
  // error response).
  deleteCategory: async (id: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/categories/${id}`);
    handleResponse(response);
  },

  upsertAgent: async (deskId: number, req: UpsertAgentRequest): Promise<DeskAgent> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/desks/${deskId}/agents`, req);
    return handleResponse(response);
  },

  removeAgent: async (deskId: number, partyId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/desks/${deskId}/agents/${partyId}`);
    handleResponse(response);
  },

  // Skills — GET/POST /v1/helpdesk/skills (backend-go/internal/modules/
  // helpdesk/helpdesk.go, handlers/config_handler.go ListSkills/CreateSkill).
  listSkills: async (includeInactive?: boolean): Promise<Skill[]> => {
    const params = includeInactive ? { includeInactive: 'true' } : undefined;
    const response = await BaseAPI.request('GET', '/v1/helpdesk/skills', null, undefined, 'application/json', params);
    return handleResponse(response);
  },

  createSkill: async (req: CreateSkillRequest): Promise<Skill> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/skills', req);
    return handleResponse(response);
  },

  listDeskFields: async (deskId: number): Promise<DeskField[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/fields`);
    return handleResponse(response);
  },

  createDeskField: async (deskId: number, req: CreateDeskFieldRequest): Promise<DeskField> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/fields`, req);
    return handleResponse(response);
  },

  updateDeskField: async (fieldId: number, req: UpdateDeskFieldRequest): Promise<DeskField> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/fields/${fieldId}`, req);
    return handleResponse(response);
  },

  // deleteDeskField maps to DELETE /v1/helpdesk/fields/:id (manage-guarded;
  // backend-go/internal/modules/helpdesk/helpdesk.go registerConfigRoutes,
  // handlers/config_handler.go DeleteDeskField).
  deleteDeskField: async (fieldId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/fields/${fieldId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // SLA policies (sla_dto.go, Phase 2 G-FE-2) — desk-scoped CRUD. Note: the
  // backend nests update/delete under the desk too (PUT/DELETE
  // /v1/helpdesk/desks/:id/sla-policies/:policyId, not a flat
  // /sla-policies/:policyId), so deskId is required on every call —
  // handlers/sla_handler.go RegisterSlaRoutes.
  // ---------------------------------------------------------------------

  listSlaPolicies: async (deskId: number): Promise<SlaPolicy[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/sla-policies`);
    return handleResponse(response);
  },

  createSlaPolicy: async (deskId: number, req: CreateSlaPolicyRequest): Promise<SlaPolicy> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/sla-policies`, req);
    return handleResponse(response);
  },

  updateSlaPolicy: async (deskId: number, policyId: number, req: UpdateSlaPolicyRequest): Promise<SlaPolicy> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/desks/${deskId}/sla-policies/${policyId}`, req);
    return handleResponse(response);
  },

  deleteSlaPolicy: async (deskId: number, policyId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/desks/${deskId}/sla-policies/${policyId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Time tracking — timers + time logs
  // ---------------------------------------------------------------------

  timerStart: async (ticketId: string): Promise<void> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/timer/start', { ticketId });
    handleResponse(response);
  },

  timerStop: async (): Promise<StopTimerResponse> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/timer/stop');
    return handleResponse(response);
  },

  timerGet: async (): Promise<TimerResponse | { active: false }> => {
    const response = await BaseAPI.request('GET', '/v1/helpdesk/timer');
    return handleResponse(response);
  },

  listTimeLogs: async (id: string): Promise<TimeLogSummary> => {
    // 403 is an EXPECTED outcome here, not an error: the backend restricts time
    // logs to agents on the ticket's desk (time spent is internal — see the Go
    // test TestGetTimeLogs_Requester_NotDeskAgent_ReturnsForbidden). The ticket
    // page treats it as "no time panel" and carries on, so `expectedStatuses`
    // stops the global interceptor throwing a "Permission Denied" dialog over an
    // otherwise working screen.
    const response = await BaseAPI.request(
      'GET',
      `/v1/helpdesk/tickets/${id}/time-logs`,
      null,
      undefined,
      'application/json',
      undefined,
      true,
      { expectedStatuses: [403] }
    );
    return handleResponse(response);
  },

  logTime: async (id: string, req: LogTimeRequest): Promise<void> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/tickets/${id}/time-logs`, req);
    handleResponse(response);
  },

  // deleteTimeLog maps to DELETE /v1/helpdesk/tickets/:id/time-logs/:entryId.
  // The backend enforces owner-only delete (Forbidden for other agents'
  // entries) — callers should surface that error to the user rather than
  // silently swallowing it.
  deleteTimeLog: async (ticketId: string, entryId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/tickets/${ticketId}/time-logs/${entryId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Ad-hoc approvals + handover (approval_dto.go, B1/B2)
  // ---------------------------------------------------------------------

  listApprovals: async (ticketId: string): Promise<Approval[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/tickets/${ticketId}/approvals`);
    return handleResponse(response);
  },

  createApproval: async (ticketId: string, req: CreateApprovalRequest): Promise<Approval> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/tickets/${ticketId}/approvals`, req);
    return handleResponse(response);
  },

  decideApproval: async (ticketId: string, approvalId: number, req: DecideRequest): Promise<Approval> => {
    const response = await BaseAPI.request(
      'POST',
      `/v1/helpdesk/tickets/${ticketId}/approvals/${approvalId}/decide`,
      req
    );
    return handleResponse(response);
  },

  cancelApproval: async (ticketId: string, approvalId: number): Promise<Approval> => {
    const response = await BaseAPI.request(
      'POST',
      `/v1/helpdesk/tickets/${ticketId}/approvals/${approvalId}/cancel`
    );
    return handleResponse(response);
  },

  requestHandover: async (ticketId: string, req: HandoverRequest): Promise<Approval> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/tickets/${ticketId}/handover`, req);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Status counts (workspace queue chips / dashboard tiles)
  // ---------------------------------------------------------------------

  statusCounts: async (deskId?: number): Promise<StatusCountsResponse> => {
    const params = deskId ? { deskId } : undefined;
    const response = await BaseAPI.request('GET', '/v1/helpdesk/statuscounts', null, undefined, 'application/json', params);
    return handleResponse(response);
  },

  // queueCounts maps to GET /v1/helpdesk/statuscounts/queues?deskId= — the
  // caller-scoped per-QUEUE ticket counts (my/unassigned/all/breached) used
  // to populate the workspace tab badges. Distinct from statusCounts above,
  // which is a per-ticket-STATUS breakdown and must not be misused for the
  // queue tab badges.
  queueCounts: async (deskId?: number): Promise<QueueCountsResponse> => {
    const params = deskId ? { deskId } : undefined;
    const response = await BaseAPI.request('GET', '/v1/helpdesk/statuscounts/queues', null, undefined, 'application/json', params);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Routing rules (routing_dto.go, Phase 2 C2/G-FE-3) — desk-scoped list/
  // create; update/delete are FLAT (not desk-nested) —
  // backend-go/internal/modules/helpdesk/helpdesk.go registerRoutingRoutes:
  //   GET/POST  /v1/helpdesk/desks/:id/routing-rules
  //   PUT       /v1/helpdesk/routing-rules/:ruleId
  //   DELETE    /v1/helpdesk/routing-rules/:ruleId
  // ---------------------------------------------------------------------

  listRoutingRules: async (deskId: number): Promise<RoutingRule[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/routing-rules`);
    return handleResponse(response);
  },

  createRoutingRule: async (deskId: number, req: CreateRoutingRuleRequest): Promise<RoutingRule> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/routing-rules`, req);
    return handleResponse(response);
  },

  updateRoutingRule: async (ruleId: number, req: UpdateRoutingRuleRequest): Promise<RoutingRule> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/routing-rules/${ruleId}`, req);
    return handleResponse(response);
  },

  deleteRoutingRule: async (ruleId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/routing-rules/${ruleId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Canned responses (canned_dto.go, Phase 2 D/G-FE-3) — desk-scoped list/
  // create; update/delete are FLAT —
  // backend-go/internal/modules/helpdesk/handlers/canned_handler.go
  // RegisterCannedRoutes:
  //   GET/POST  /v1/helpdesk/desks/:id/canned-responses
  //   PUT       /v1/helpdesk/canned-responses/:cannedId
  //   DELETE    /v1/helpdesk/canned-responses/:cannedId
  // ---------------------------------------------------------------------

  listCannedResponses: async (deskId: number): Promise<CannedResponse[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/canned-responses`);
    return handleResponse(response);
  },

  createCannedResponse: async (deskId: number, req: CreateCannedResponseRequest): Promise<CannedResponse> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/canned-responses`, req);
    return handleResponse(response);
  },

  updateCannedResponse: async (cannedId: number, req: UpdateCannedResponseRequest): Promise<CannedResponse> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/canned-responses/${cannedId}`, req);
    return handleResponse(response);
  },

  deleteCannedResponse: async (cannedId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/canned-responses/${cannedId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Intake forms (intake_dto.go, Phase 2 F/G-FE-3) — desk-scoped list/
  // create; update/delete are FLAT; get-by-key + submit are keyed, not
  // desk-nested —
  // backend-go/internal/modules/helpdesk/handlers/intake_handler.go
  // RegisterIntakeRoutes:
  //   GET/POST  /v1/helpdesk/desks/:id/intake-forms
  //   PUT       /v1/helpdesk/intake-forms/:formId
  //   DELETE    /v1/helpdesk/intake-forms/:formId
  //   GET       /v1/helpdesk/intake-forms/:key
  //   POST      /v1/helpdesk/intake-forms/:key/tickets
  // NOTE: all intake routes sit under the /v1/helpdesk group, which requires
  // JWT + TenantContextMiddleware — there is currently no true anonymous/
  // public path even though allowAnonymous exists on the DTO (the handler's
  // own doc comment says public/anonymous portal rendering is deferred to
  // Phase 3). See final report for this gap.
  // ---------------------------------------------------------------------

  listIntakeForms: async (deskId: number): Promise<IntakeForm[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/intake-forms`);
    return handleResponse(response);
  },

  createIntakeForm: async (deskId: number, req: CreateIntakeFormRequest): Promise<IntakeForm> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/intake-forms`, req);
    return handleResponse(response);
  },

  updateIntakeForm: async (formId: number, req: UpdateIntakeFormRequest): Promise<IntakeForm> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/intake-forms/${formId}`, req);
    return handleResponse(response);
  },

  deleteIntakeForm: async (formId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/intake-forms/${formId}`);
    handleResponse(response);
  },

  getIntakeForm: async (key: string): Promise<IntakeForm> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/intake-forms/${key}`);
    return handleResponse(response);
  },

  submitIntakeTicket: async (key: string, req: SubmitIntakeTicketRequest): Promise<Ticket> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/intake-forms/${key}/tickets`, req);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // CSAT (customer satisfaction survey) — Phase 3.
  // ---------------------------------------------------------------------

  // submitCsat is the one PUBLIC (unauthenticated) call in this file — the
  // survey link's token IS the caller's only credential, so useAuth=false
  // (6th arg) skips BaseAPI.getAuthHeaders (which would throw if no session
  // exists — an anonymous CSAT respondent never has one).
  //
  // A double-submit is a normal, expected outcome (not an error): the
  // backend returns HTTP 200 with `{ message: "..." }` and no `data`/`id`
  // field for that case (see csat_handler.go's Submit), so this can't reuse
  // handleResponse() — that helper treats "message present, no data" as an
  // exception. alreadySubmitted distinguishes the two 200 outcomes for the
  // page to render the right state.
  // NOTE: path is /public/helpdesk/csat/:token, NOT /v1/helpdesk/csat/:token
  // — see helpdesk.go's RegisterRoutesWithPermissions doc comment on why no
  // /v1/* path can ever be public in this backend (a global JWT gate is
  // bound to the whole "/v1" prefix before the helpdesk module registers a
  // single route).
  submitCsat: async (
    token: string,
    req: SubmitCsatRequest
  ): Promise<{ alreadySubmitted: boolean; message?: string; data?: CsatResponse }> => {
    const response = await BaseAPI.request('POST', `/public/helpdesk/csat/${token}`, req, null, 'application/json', null, false);
    if (!response) {
      throw new Error('Network error or server is unreachable');
    }
    if (response.status && response.status >= 400) {
      throw new Error(response.message || `Request failed with status ${response.status}`);
    }
    const body = response.data;
    if (body && typeof body === 'object' && !('id' in body) && 'message' in body) {
      return { alreadySubmitted: true, message: body.message };
    }
    return { alreadySubmitted: false, data: body as CsatResponse };
  },

  getCsatForTicket: async (ticketId: string): Promise<CsatAggregateResponse> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/tickets/${ticketId}/csat`);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Desk template catalog (Phase 3 / U3) — CR-maintained starter kits.
  // backend-go/internal/modules/helpdesk/helpdesk.go registerTemplateRoutes:
  //   GET  /v1/helpdesk/templates/desks
  //   GET  /v1/helpdesk/templates/desks/:id
  //   POST /v1/helpdesk/desks/from-template/:templateId
  // ---------------------------------------------------------------------

  listTemplateDesks: async (): Promise<TemplateDesk[]> => {
    const response = await BaseAPI.request('GET', '/v1/helpdesk/templates/desks');
    return handleResponse(response);
  },

  getTemplateDeskTree: async (templateId: number): Promise<TemplateDeskTree> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/templates/desks/${templateId}`);
    return handleResponse(response);
  },

  createDeskFromTemplate: async (templateId: number, req: CreateDeskFromTemplateRequest): Promise<Desk> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/from-template/${templateId}`, req);
    return handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Email identities (email_identity_dto.go, Phase 3 / Part B) — desk-scoped
  // list/create; update/delete are FLAT (not desk-nested) —
  // backend-go/internal/modules/helpdesk/handlers/email_identity_handler.go
  // RegisterEmailIdentityRoutes:
  //   GET/POST  /v1/helpdesk/desks/:id/email-identities
  //   PUT       /v1/helpdesk/email-identities/:identityId
  //   DELETE    /v1/helpdesk/email-identities/:identityId
  // ---------------------------------------------------------------------

  listEmailIdentities: async (deskId: number): Promise<EmailIdentity[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/email-identities`);
    return handleResponse(response);
  },

  createEmailIdentity: async (deskId: number, req: CreateEmailIdentityRequest): Promise<EmailIdentity> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/email-identities`, req);
    return handleResponse(response);
  },

  updateEmailIdentity: async (identityId: number, req: UpdateEmailIdentityRequest): Promise<EmailIdentity> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/email-identities/${identityId}`, req);
    return handleResponse(response);
  },

  deleteEmailIdentity: async (identityId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/email-identities/${identityId}`);
    handleResponse(response);
  },

  // ---------------------------------------------------------------------
  // Connectors (connector_dto.go, Phase 3 / U5) — desk-scoped list/create;
  // update/delete are FLAT —
  // backend-go/internal/modules/helpdesk/handlers/connector_handler.go
  // RegisterConnectorRoutes:
  //   GET/POST  /v1/helpdesk/desks/:id/connectors
  //   PUT       /v1/helpdesk/connectors/:connectorId
  //   DELETE    /v1/helpdesk/connectors/:connectorId
  // The public inbound Jira webhook is a SEPARATE, unauthenticated route —
  // POST /public/helpdesk/connectors/jira/webhook/:connectorId — not part of
  // this authenticated admin surface; see IntegrationsConnectors.tsx for
  // where the FE displays that URL for the admin to paste into Jira.
  // ---------------------------------------------------------------------

  listConnectors: async (deskId: number): Promise<Connector[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/desks/${deskId}/connectors`);
    return handleResponse(response);
  },

  createConnector: async (deskId: number, req: CreateConnectorRequest): Promise<Connector> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/desks/${deskId}/connectors`, req);
    return handleResponse(response);
  },

  updateConnector: async (connectorId: number, req: UpdateConnectorRequest): Promise<Connector> => {
    const response = await BaseAPI.request('PUT', `/v1/helpdesk/connectors/${connectorId}`, req);
    return handleResponse(response);
  },

  deleteConnector: async (connectorId: number): Promise<void> => {
    const response = await BaseAPI.request('DELETE', `/v1/helpdesk/connectors/${connectorId}`);
    handleResponse(response);
  }
};

// ---------------------------------------------------------------------------
// Client Portal Helpdesk API — Wave V wiring fix.
//
// The Client Portal Helpdesk pages (src/content/applications/ClientPortal/
// Helpdesk/*) must NEVER call the internal /v1/helpdesk/tickets/* /
// /v1/helpdesk/desks/* endpoints above: those are Casbin-gated on
// helpdesk.tickets.{view,create,update} / helpdesk.manage, and the Client /
// Client Admin roles hold zero Casbin rows for those resources — every such
// call 403s. Backend fix: backend-go/internal/modules/helpdesk/
// portal_ticket_routes.go registers a dedicated /v1/helpdesk/portal/tickets/*
// + /v1/helpdesk/portal/desks/:id* surface gated by
// middleware.ClientPortalGuard instead (the same guard GET
// /v1/helpdesk/portal/desks already used, and getPortalDesks() above already
// calls) — thin wrappers over the SAME usecases, with server-forced
// requester-only scoping on the list endpoint and desk-audience checks on
// every desk-scoped call. Kept as a SEPARATE export (not merged into
// HelpdeskAPI above) so the internal Helpdesk/ (employee) app's calls are
// never at risk of being repointed by a future edit here.
// ---------------------------------------------------------------------------
export const PortalHelpdeskAPI = {
  searchTickets: async (req: SearchTicketsRequest): Promise<SearchTicketsResponse> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/portal/tickets/search', req);
    return handleResponse(response);
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/portal/tickets/${id}`);
    return handleResponse(response);
  },

  createTicket: async (req: CreateTicketRequest): Promise<Ticket> => {
    const response = await BaseAPI.request('POST', '/v1/helpdesk/portal/tickets', req);
    return handleResponse(response);
  },

  setStatus: async (id: string, status: TicketStatus, substatus?: string | null): Promise<Ticket> => {
    const req: UpdateStatusRequest = { status, substatus: substatus ?? undefined };
    const response = await BaseAPI.request('PATCH', `/v1/helpdesk/portal/tickets/${id}/status`, req);
    return handleResponse(response);
  },

  listMessages: async (id: string): Promise<TicketMessage[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/portal/tickets/${id}/messages`);
    return handleResponse(response);
  },

  postMessage: async (id: string, req: CreateMessageRequest): Promise<TicketMessage> => {
    const response = await BaseAPI.request('POST', `/v1/helpdesk/portal/tickets/${id}/messages`, req);
    return handleResponse(response);
  },

  getCsatForTicket: async (ticketId: string): Promise<CsatAggregateResponse> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/portal/tickets/${ticketId}/csat`);
    return handleResponse(response);
  },

  // getPortalDesks is unchanged — HelpdeskAPI.getPortalDesks() already calls
  // the portal-gated GET /v1/helpdesk/portal/desks. Re-exported here so
  // portal pages can import everything they need from one module if desired;
  // ClientPortal/Helpdesk/RaiseTicket/index.tsx currently still calls
  // HelpdeskAPI.getPortalDesks() directly, which is equally correct.
  getPortalDesks: HelpdeskAPI.getPortalDesks,

  getDesk: async (id: number): Promise<DeskDetail> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/portal/desks/${id}`);
    return handleResponse(response);
  },

  listCategoryTree: async (deskId: number): Promise<CategoryNode[]> => {
    const response = await BaseAPI.request('GET', `/v1/helpdesk/portal/desks/${deskId}/categories/tree`);
    return handleResponse(response);
  }
};

export type { TimeLogEntry };
